/**
 * Teacher Classes Page
 *
 * Page for teachers to manage their class groups.
 * Allows editing Google Meet and Whiteboard links.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "../../components/ui";
import { useTeacherClasses, useUpdateClassLinks } from "../../hooks/useTeacher";
import type { ClassGroupDetail, ClassGroupStudent } from "../../types/teacher";

const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];

// Format schedule days for display
function formatSchedule(
  schedule: any | null,
): string {
  if (!schedule) return "Not set";

  const dayMap: Record<string, string> = {
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
    sunday: "Sun",
    Mon: "Mon",
    Tue: "Tue",
    Wed: "Wed",
    Thu: "Thu",
    Fri: "Fri",
    Sat: "Sat",
    Sun: "Sun",
  };

  // New format: array of day schedules
  if (Array.isArray(schedule)) {
    if (schedule.length === 0) return "Not set";
    const scheduleItems = schedule
      .map((item) => {
        const day = dayMap[item.day] || item.day;
        return `${day} at ${item.time}`;
      })
      .join(", ");
    return scheduleItems;
  }

  // Legacy format: single time for all days
  if (schedule.days && schedule.time) {
    const days = schedule.days.map((d: string) => dayMap[d] || d).join(", ");
    return `${days} at ${schedule.time}`;
  }

  return "Not set";
}

// Get class type label
function getClassTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    individual: "Individual",
    duo: "Duo",
    group: "Group",
  };
  return labels[type] || type;
}

// Student Contact Modal
function StudentContactModal({
  student,
  onClose,
}: {
  student: ClassGroupStudent;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose}></div>

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl overflow-hidden m-4">
        {/* Header */}
        <div className="border-b border-earth-100 px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-earth-800">
              Student Contact
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-earth-100 rounded-lg transition-colors"
            >
              <i className="ri-close-line text-xl text-earth-500"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Student Info */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-earth-100">
            <div className="w-14 h-14 bg-earth-100 rounded-lg flex items-center justify-center">
              <span className="text-xl font-bold text-earth-700">
                {student.fullName.charAt(0)}
              </span>
            </div>
            <div>
              <h3 className="text-base font-semibold text-earth-800">
                {student.fullName}
              </h3>
              <p className="text-sm text-earth-700">Student</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Email */}
            {student.email && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <i className="ri-mail-line text-earth-400 text-base"></i>
                  <p className="text-xs font-medium text-earth-700 uppercase tracking-wide">
                    Email
                  </p>
                </div>
                <a
                  href={`mailto:${student.email}`}
                  className="block text-sm text-earth-800 hover:text-earth-900 transition-colors break-all pl-6"
                >
                  {student.email}
                </a>
              </div>
            )}

            {/* Phone */}
            {student.phone && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <i className="ri-phone-line text-earth-400 text-base"></i>
                  <p className="text-xs font-medium text-earth-700 uppercase tracking-wide">
                    Phone
                  </p>
                </div>
                <a
                  href={`https://wa.me/${student.phone.replace(/\D/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-sm text-earth-800 hover:text-earth-900 transition-colors pl-6"
                >
                  {student.phone}
                </a>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2 mt-6 pt-6 border-t border-earth-100">
            {student.phone && (
              <a
                href={`https://wa.me/${student.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <i className="ri-whatsapp-line"></i>
                Message on WhatsApp
              </a>
            )}
            {student.email && (
              <a
                href={`mailto:${student.email}`}
                className="flex items-center justify-center gap-2 w-full py-2.5 border border-earth-200 hover:bg-earth-50 text-earth-700 rounded-lg text-sm font-medium transition-colors"
              >
                <i className="ri-mail-send-line"></i>
                Send Email
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TeacherClassesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const levelFilter = searchParams.get("level") || "";

  const [selectedClass, setSelectedClass] = useState<ClassGroupDetail | null>(
    null,
  );
  const [selectedStudent, setSelectedStudent] =
    useState<ClassGroupStudent | null>(null);
  const [editMode, setEditMode] = useState<"meet" | "whiteboard" | null>(null);
  const [editValue, setEditValue] = useState("");
  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle");

  const {
    data: classes,
    isLoading,
    error,
  } = useTeacherClasses({
    level: levelFilter || undefined,
    // Do NOT pass teacher filter - backend handles filtering by logged-in teacher
    teacher: undefined,
  });

  const updateClassLinks = useUpdateClassLinks();

  // Sync selectedClass with updated data from the classes list
  useEffect(() => {
    if (selectedClass && classes) {
      const updatedClass = classes.find((c) => c.id === selectedClass.id);
      if (updatedClass) {
        // Only update if the data has actually changed
        if (
          updatedClass.meetLink !== selectedClass.meetLink ||
          updatedClass.whiteboardUrl !== selectedClass.whiteboardUrl
        ) {
          setSelectedClass(updatedClass);
        }
      }
    }
  }, [classes, selectedClass]);

  // Reset edit mode when class changes
  useEffect(() => {
    setEditMode(null);
    setEditValue("");
    setSaveStatus("idle");
  }, [selectedClass?.id]);

  const handleLevelChange = (level: string) => {
    const params = new URLSearchParams(searchParams);
    if (level) {
      params.set("level", level);
    } else {
      params.delete("level");
    }
    setSearchParams(params);
  };

  const handleStartEdit = (mode: "meet" | "whiteboard") => {
    setEditMode(mode);
    if (mode === "meet") {
      setEditValue(selectedClass?.meetLink || "");
    } else {
      setEditValue(selectedClass?.whiteboardUrl || "");
    }
    setSaveStatus("idle");
  };

  const handleSave = async () => {
    if (!selectedClass) return;

    setSaveStatus("saving");

    try {
      const result = await updateClassLinks.mutateAsync({
        id: selectedClass.id,
        data:
          editMode === "meet"
            ? { meetLink: editValue || undefined }
            : { whiteboardUrl: editValue || undefined },
      });

      // Update selected class with result
      setSelectedClass(result);

      setSaveStatus("success");
      setTimeout(() => {
        setEditMode(null);
        setSaveStatus("idle");
      }, 1500);
    } catch (err) {
      console.error("Error saving class links:", err);
      setSaveStatus("error");
    }
  };

  const handleCancel = () => {
    setEditMode(null);
    setEditValue("");
    setSaveStatus("idle");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-earth-400"></i>
          </div>
          <p className="text-earth-600 font-medium">Error loading classes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/teacher"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
            Manage Classes
          </h1>
          <p className="text-earth-700 text-sm mt-1">
            View and edit Google Meet and Whiteboard links for each class
          </p>
        </div>

        {/* Level Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => handleLevelChange("")}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              !levelFilter
                ? "bg-earth-800 text-white"
                : "bg-earth-100 text-earth-600 hover:bg-earth-200"
            }`}
          >
            All
          </button>
          {LEVELS.map((level) => (
            <button
              key={level}
              onClick={() => handleLevelChange(level)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                levelFilter === level
                  ? "bg-earth-800 text-white"
                  : "bg-earth-100 text-earth-600 hover:bg-earth-200"
              }`}
            >
              {level}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Classes List */}
          <div className="lg:col-span-1 space-y-3">
            {classes && classes.length > 0 ? (
              classes.map((classGroup) => (
                <button
                  key={classGroup.id}
                  onClick={() => setSelectedClass(classGroup)}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    selectedClass?.id === classGroup.id
                      ? "bg-olive-50 border-olive-300 ring-2 ring-olive-200"
                      : "bg-white border-earth-200 hover:border-earth-300"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-earth-800 truncate">
                      {classGroup.name}
                    </h3>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium shrink-0 ${
                        classGroup.classType === "individual"
                          ? "bg-blue-100 text-blue-700"
                          : classGroup.classType === "duo"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {getClassTypeLabel(classGroup.classType)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-earth-700 mb-1">
                    <span className="font-medium text-earth-700">
                      {classGroup.levelCode}
                    </span>
                    <span className="text-earth-300">•</span>
                    <span>{formatSchedule(classGroup.schedule)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-earth-700">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>
                      {classGroup.studentCount}/{classGroup.maxStudents}
                    </span>
                    {classGroup.meetLink && (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <i className="ri-video-chat-line"></i>
                        Meet
                      </span>
                    )}
                    {classGroup.whiteboardUrl && (
                      <span className="flex items-center gap-1 text-purple-600">
                        <i className="ri-pencil-ruler-2-line"></i>
                        Whiteboard
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="text-center py-12 bg-white rounded-xl border border-earth-200">
                <i className="ri-folder-open-line text-4xl text-earth-300 mb-2"></i>
                <p className="text-earth-700">No classes found</p>
              </div>
            )}
          </div>

          {/* Selected Class Details */}
          <div className="lg:col-span-2">
            {selectedClass ? (
              <div className="bg-white rounded-xl border border-earth-200 p-5 sm:p-6">
                {/* Class Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-earth-800">
                      {selectedClass.name}
                    </h2>
                    <p className="text-earth-700 text-sm mt-1">
                      {selectedClass.levelCode} - {selectedClass.levelName} •{" "}
                      {getClassTypeLabel(selectedClass.classType)}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded-lg text-sm font-medium ${
                      selectedClass.isActive
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-earth-100 text-earth-700"
                    }`}
                  >
                    {selectedClass.isActive ? "Active" : "Inactive"}
                  </span>
                </div>

                {/* Schedule & Teacher Info */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  <div className="p-3 bg-earth-50 rounded-lg">
                    <p className="text-xs text-earth-700 uppercase tracking-wide mb-1">
                      Schedule
                    </p>
                    <p className="font-medium text-earth-800">
                      {formatSchedule(selectedClass.schedule)}
                    </p>
                  </div>
                  <div className="p-3 bg-earth-50 rounded-lg">
                    <p className="text-xs text-earth-700 uppercase tracking-wide mb-1">
                      Teacher
                    </p>
                    <p className="font-medium text-earth-800">
                      {selectedClass.teacherName || "Not assigned"}
                    </p>
                  </div>
                </div>

                {/* Links Section */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-earth-700 uppercase tracking-wide">
                    Class Links
                  </h3>

                  {/* Google Meet Link */}
                  <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-500 rounded-lg flex items-center justify-center">
                          <i className="ri-video-chat-line text-white"></i>
                        </div>
                        <span className="font-medium text-emerald-900">
                          Google Meet
                        </span>
                      </div>
                      {editMode !== "meet" && (
                        <button
                          onClick={() => handleStartEdit("meet")}
                          className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Edit
                        </button>
                      )}
                    </div>

                    {editMode === "meet" ? (
                      <div className="mt-3">
                        <input
                          type="url"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="https://meet.google.com/xxx-xxxx-xxx"
                          className="w-full px-3 py-2 border border-emerald-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                        />
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={handleSave}
                            disabled={saveStatus === "saving"}
                            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {saveStatus === "saving" ? (
                              <span className="flex items-center gap-2">
                                <Spinner size="sm" /> Saving...
                              </span>
                            ) : saveStatus === "success" ? (
                              <span className="flex items-center gap-1">
                                <i className="ri-check-line"></i> Saved!
                              </span>
                            ) : (
                              "Save"
                            )}
                          </button>
                          <button
                            onClick={handleCancel}
                            className="px-4 py-2 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        {saveStatus === "error" && (
                          <p className="text-red-600 text-sm mt-2">
                            Error saving. Please try again.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2">
                        {selectedClass.meetLink ? (
                          <a
                            href={selectedClass.meetLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-emerald-700 hover:text-emerald-800 underline break-all"
                          >
                            {selectedClass.meetLink}
                          </a>
                        ) : (
                          <p className="text-sm text-emerald-600 italic">
                            No link configured
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Whiteboard Link (Class Group specific) */}
                  <div className="p-4 bg-purple-50 border border-purple-100 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                          <i className="ri-pencil-ruler-2-line text-white"></i>
                        </div>
                        <span className="font-medium text-purple-900">
                          Whiteboard
                        </span>
                      </div>
                      {editMode !== "whiteboard" && (
                        <button
                          onClick={() => handleStartEdit("whiteboard")}
                          className="px-3 py-1.5 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg text-sm font-medium transition-colors"
                        >
                          <i className="ri-edit-line mr-1"></i>
                          Edit
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-purple-600 mb-2">
                      Whiteboard link for this class group
                    </p>

                    {editMode === "whiteboard" ? (
                      <div className="mt-3">
                        <input
                          type="url"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          placeholder="https://www.canva.com/design/..."
                          className="w-full px-3 py-2 border border-purple-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
                        />
                        <div className="flex items-center gap-2 mt-3">
                          <button
                            onClick={handleSave}
                            disabled={saveStatus === "saving"}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                          >
                            {saveStatus === "saving" ? (
                              <span className="flex items-center gap-2">
                                <Spinner size="sm" /> Saving...
                              </span>
                            ) : saveStatus === "success" ? (
                              <span className="flex items-center gap-1">
                                <i className="ri-check-line"></i> Saved!
                              </span>
                            ) : (
                              "Save"
                            )}
                          </button>
                          <button
                            onClick={handleCancel}
                            className="px-4 py-2 text-earth-600 hover:text-earth-800 text-sm font-medium transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                        {saveStatus === "error" && (
                          <p className="text-red-600 text-sm mt-2">
                            Error saving. Please try again.
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="mt-2">
                        {selectedClass.whiteboardUrl ? (
                          <a
                            href={selectedClass.whiteboardUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-purple-700 hover:text-purple-800 underline break-all"
                          >
                            {selectedClass.whiteboardUrl}
                          </a>
                        ) : (
                          <p className="text-sm text-purple-600 italic">
                            No link configured
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Students in Class */}
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-earth-700 uppercase tracking-wide mb-3">
                    Students ({selectedClass.students.length}/
                    {selectedClass.maxStudents})
                  </h3>
                  {selectedClass.students.length > 0 ? (
                    <div className="space-y-2">
                      {selectedClass.students.map((student) => (
                        <button
                          key={student.id}
                          onClick={() => setSelectedStudent(student)}
                          className="w-full flex items-center gap-3 p-3 bg-earth-50 hover:bg-olive-50 rounded-lg transition-colors group"
                        >
                          <div className="w-8 h-8 bg-earth-200 group-hover:bg-olive-200 rounded-full flex items-center justify-center text-earth-600 group-hover:text-olive-700 font-semibold text-sm transition-colors">
                            {student.fullName.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className="font-medium text-earth-800 group-hover:text-olive-800 truncate transition-colors">
                              {student.fullName}
                            </p>
                            <p className="text-xs text-earth-700 truncate">
                              {student.email}
                            </p>
                          </div>
                          <i className="ri-arrow-right-s-line text-earth-400 group-hover:text-olive-600 transition-colors"></i>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-earth-700 text-sm italic">
                      No students in this class
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-earth-200 p-12 text-center">
                <div className="w-16 h-16 bg-earth-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-cursor-line text-2xl text-earth-400"></i>
                </div>
                <p className="text-earth-600 font-medium">Select a class</p>
                <p className="text-earth-700 text-sm mt-1">
                  Click on a class in the list to view details and edit links
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Student Contact Modal */}
      {selectedStudent && (
        <StudentContactModal
          student={selectedStudent}
          onClose={() => setSelectedStudent(null)}
        />
      )}
    </div>
  );
}

export default TeacherClassesPage;
